import { type Registry as Services, service } from '@ember/service';
import Component from '@glimmer/component';
import type { CustomEmberTableColumn } from '@mylighthouse/prism-ember/types/components/prism/table';
import GeneralPercentageChangeValue from 'lumen/components/general/percentage-change-value';
import type { Occupancy, Period, PredictedOccupancy } from 'lumen/models';

export interface HotelOccupancyMonthColumn extends CustomEmberTableColumn {
  isForecast: boolean;
}

interface HotelPricingAssistantTableCellsTdHotelOccupancyMonthSignature {
  Args: {
    column: HotelOccupancyMonthColumn;
    value: Period;
  };
}

/* My OTB as the week view's day header shows it, or Forecast as the day tooltip's
   My forecast; the period decides when a change shows. */
export default class HotelPricingAssistantTableCellsTdHotelOccupancyMonth extends Component<HotelPricingAssistantTableCellsTdHotelOccupancyMonthSignature> {
  @service('hotel-and-market')
  declare hotelAndMarketService: Services['hotel-and-market'];
  @service declare intl: Services['intl'];
  @service('pricing-assistant-data-request-service')
  declare pricingAssistantDataRequestService: Services['pricing-assistant-data-request-service'];

  get change(): number | null | undefined {
    return this.args.column.isForecast
      ? this.args.value.predictedOccupancyChange
      : this.args.value.occupancyChange;
  }

  get displayValue(): string {
    return (
      this.occupancyData?.formattedOccupancyPercentage ??
      this.intl.t('general.missing')
    );
  }

  /* The request task also fetches holidays and events, so the pulse can outlast occupancy. */
  get isLoading(): boolean {
    return this.pricingAssistantDataRequestService
      .pricingAssistantGeneralDataRequestsTask.isRunning;
  }

  get isMissing(): boolean {
    return this.occupancyData?.occupancyPercentage == null;
  }

  get isPast(): boolean {
    return this.hotelAndMarketService.isPast(this.args.value.fromDate);
  }

  get occupancyData(): Occupancy | PredictedOccupancy | undefined {
    const { day } = this.args.value;

    return this.args.column.isForecast ? day.predictedOccupancy : day.occupancy;
  }

  get showChange(): boolean {
    return this.args.column.isForecast
      ? this.args.value.showPredictedOccupancyChange
      : this.args.value.showOccupancyChange;
  }

  get testId(): string {
    return this.args.column.isForecast
      ? 'td-hotel-forecast-month'
      : 'td-hotel-otb-month';
  }

  <template>
    {{#if this.isLoading}}
      <div class="loading-pulse" data-test-id="{{this.testId}}-loading"></div>
    {{else}}
      <div
        class="mr-500 flex items-center justify-end gap-50
          {{if
            this.isPast
            'text-disabled'
            (if this.isMissing 'text-neutral-subdued')
          }}"
        data-test-id={{this.testId}}
      >
        <span data-test-id="{{this.testId}}-value">{{this.displayValue}}</span>
        {{#if this.showChange}}
          <span data-test-id="{{this.testId}}-change">
            <GeneralPercentageChangeValue @changeValue={{this.change}} />
          </span>
        {{/if}}
      </div>
    {{/if}}
  </template>
}
